import { Response } from '@/api'
import { ApiError, handleApiError } from '@/handleApiError'
import api from '@/modules/order/api'
import { getRelatedStatuses } from '@/modules/order/helpers'
import { OrderPreviewColumns, OrderPreviewColumnsKey } from '@/modules/order/types'
import { t } from '@/plugins/i18n.ts'
import { PortalOrderStatus, StorefrontOrderSort } from '@/types/index'
import { defineStore } from 'pinia'

const PAGE_SIZE = 20

interface OrderPreviewsState {
  columns: OrderPreviewColumns
}

const initialState = (): OrderPreviewsState => {
  return {
    columns: {
      ARRIVED: {
        orderStatus: PortalOrderStatus.ARRIVED,
        orderPreviews: [],
        count: 0,
        pages: 0,
        loading: false,
        loadingNextPage: false
      },
      PROCESSING: {
        orderStatus: PortalOrderStatus.PROCESSING,
        orderPreviews: [],
        count: 0,
        pages: 0,
        loading: false,
        loadingNextPage: false
      },
      ADVISED: {
        orderStatus: PortalOrderStatus.ADVISED,
        orderPreviews: [],
        count: 0,
        pages: 0,
        loading: false,
        loadingNextPage: false
      },
      INVOICED: {
        orderStatus: PortalOrderStatus.INVOICED,
        orderPreviews: [],
        count: 0,
        pages: 0,
        loading: false,
        loadingNextPage: false
      },
      ERROR: {
        orderStatus: PortalOrderStatus.ERROR,
        orderPreviews: [],
        count: 0,
        pages: 0,
        loading: false,
        loadingNextPage: false
      }
    }
  }
}

const useOrderPreviews = defineStore('orderPreviews', {
  state: () => initialState(),
  getters: {
    loadingNextPage: (state) => {
      return Object.values(state.columns).some((column) => column.loadingNextPage)
    }
  },
  actions: {
    async getOrderPreviews(status: OrderPreviewColumnsKey, page: number) {
      const column = this.columns[status]
      column.loading = true

      const statuses = getRelatedStatuses(status as PortalOrderStatus)

      return api
        .getOrderPreviews(page, PAGE_SIZE, StorefrontOrderSort.LAST_MODIFIED, statuses)
        .then(({ data }) => {
          column.orderPreviews = data.items
          column.count = data.totalCount
          column.pages = data.pageCount
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'CUSTOM',
            customHandler: 'OrdersGrid',
            customMessage: t('error.ordersNotFound')
          })
        })
        .finally(() => {
          column.loading = false
        })
    },
    async getAdditionalPage(status: OrderPreviewColumnsKey, page: number) {
      const column = this.columns[status]

      if (column.pages >= page) {
        column.loadingNextPage = true
        const statuses = getRelatedStatuses(status as PortalOrderStatus)

        return api
          .getOrderPreviews(page, PAGE_SIZE, StorefrontOrderSort.LAST_MODIFIED, statuses)
          .then(({ data }) => {
            column.orderPreviews.push(...data.items)
            column.count = data.totalCount
            column.pages = data.pageCount
          })
          .catch((error: ApiError) => {
            handleApiError(error, {
              appearance: 'CUSTOM',
              customHandler: 'OrdersGrid',
              customMessage: t('error.ordersNotFound')
            })
          })
          .finally(() => {
            column.loadingNextPage = false
          })
      }
    },
    async archiveAllErrorOrders() {
      const promises: Promise<Response<string>>[] = []

      this.columns.ERROR.orderPreviews.forEach((order) => {
        promises.push(api.updateOrder(order.id, { isArchived: true, timestamp: order.timeStamp }))
      })

      return Promise.all(promises)
        .catch(handleApiError)
        .finally(() => {
          this.getOrderPreviews('ERROR', 1)
        })
    }
  }
})

export default useOrderPreviews
